import type { KeyboardEvent } from 'react'
import { getThumbnailUrl } from '../../api/useAssetUrl'
import type { MentionCandidate } from '../canvas-v2/nodes/shared/mentionUtils'
import { NODE_TYPE_ICON } from '../canvas-v2/nodes/shared/nodeTypeIcons'
import { createNadDebug } from '../../utils/debugLog'

const mentionChipOverflowLog = createNadDebug('dw:mention-chip-overflow')

/** Canonical mention-chip presentation shared by editable DOM and React views. */
export const MENTION_CHIP_PRESENTATION = {
  rootClassName:
    'inline-flex max-w-full min-w-0 items-center gap-1 rounded-md border border-chrome/25 bg-chrome/15 px-1.5 py-0.5 text-xs font-medium text-chrome align-baseline mx-0.5',
  thumbnailClassName:
    'w-3.5 h-3.5 rounded-sm object-cover flex-shrink-0 pointer-events-none',
  labelClassName:
    'inline-flex min-w-0 max-w-48 flex-1 items-center gap-0.5 cursor-pointer hover:underline',
  iconClassName: 'pointer-events-none flex-shrink-0 inline-flex w-3 h-3 text-chrome/70',
  fallbackIconClassName: 'pointer-events-none flex-shrink-0 text-xs font-medium',
  textClassName: 'pointer-events-none min-w-0 truncate',
  removeClassName:
    'ml-0.5 flex-shrink-0 text-chrome/60 transition-colors hover:text-chrome leading-none',
}

/** Record the overflow policy without copying user-authored node titles into logs. */
export function logMentionChipPresentation(candidate: MentionCandidate, surface: string): void {
  mentionChipOverflowLog({
    event: 'mention-chip:ellipsis-style-applied',
    feature: 'mention-chip-overflow',
    trace_id: `mention-chip:${candidate.id}`,
    node_id: candidate.id,
    label_length: candidate.label.length,
    surface,
    policy: 'ellipsis',
    label_max_width_token: 'max-w-48',
    full_label_preserved: true,
  })
}

/** Record that visual truncation does not change the serialized node reference. */
export function logMentionChipSerialization(nodeId: string): void {
  mentionChipOverflowLog({
    event: 'mention-chip:full-node-id-serialized',
    feature: 'mention-chip-overflow',
    trace_id: `mention-chip:${nodeId}`,
    node_id: nodeId,
    serialization_policy: 'full-node-id',
  })
}

/** Keep failed thumbnails from changing the chip's layout in either renderer. */
export function hideMentionThumbnail(image: HTMLImageElement): void {
  image.style.display = 'none'
}

export interface ReadOnlyMentionChipProps {
  candidate: MentionCandidate
  projectId?: string
  testId: string
  title: string
  onActivate?: (nodeId: string) => void
}

/** Canonical mention chip for serialized/read-only content (no remove control). */
export function ReadOnlyMentionChip({
  candidate,
  projectId,
  testId,
  title,
  onActivate,
}: ReadOnlyMentionChipProps) {
  logMentionChipPresentation(candidate, 'read-only-mention')
  const thumbnailUrl = candidate.thumbnailPath && projectId
    ? getThumbnailUrl(projectId, candidate.thumbnailPath, 0)
    : undefined
  const icon = NODE_TYPE_ICON[candidate.type]
  const activate = () => onActivate?.(candidate.id)
  const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (!onActivate || (event.key !== 'Enter' && event.key !== ' ')) return
    event.preventDefault()
    activate()
  }

  return (
    <span
      contentEditable={false}
      data-mention-pill="true"
      data-node-id={candidate.id}
      data-label={candidate.label}
      data-icon={candidate.icon}
      data-testid={testId}
      className={MENTION_CHIP_PRESENTATION.rootClassName}
      role={onActivate ? 'button' : undefined}
      tabIndex={onActivate ? 0 : undefined}
      title={title}
      onClick={activate}
      onKeyDown={handleKeyDown}
    >
      {thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt=""
          className={MENTION_CHIP_PRESENTATION.thumbnailClassName}
          onError={(event) => hideMentionThumbnail(event.currentTarget)}
        />
      )}
      <span
        data-mention-chip-label="true"
        data-testid={`mention-chip-label-${candidate.id}`}
        className={MENTION_CHIP_PRESENTATION.labelClassName}
        title={candidate.label}
      >
        {icon ? (
          <span className={MENTION_CHIP_PRESENTATION.iconClassName}>{icon}</span>
        ) : candidate.icon ? (
          <span className={MENTION_CHIP_PRESENTATION.fallbackIconClassName}>
            {candidate.icon}
          </span>
        ) : null}
        <span className={MENTION_CHIP_PRESENTATION.textClassName}>{candidate.label}</span>
      </span>
    </span>
  )
}
